import { DatePipe, NgTemplateOutlet } from "@angular/common";
import {
  AfterViewInit,
  Component,
  computed,
  input,
  output,
  signal,
  TemplateRef,
  ViewChild,
} from "@angular/core";
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";
import { MatPaginator, MatPaginatorModule, PageEvent } from "@angular/material/paginator";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { MatSort, MatSortModule, Sort, SortDirection } from "@angular/material/sort";
import { MatTableDataSource, MatTableModule } from "@angular/material/table";
import { MatTooltipModule } from "@angular/material/tooltip";
import { Router, RouterLink } from "@angular/router";
import { ImageUrlDirective } from "../../../directives";
import {
  TableAction,
  TableColumn,
  TableConfig,
  TableSortEvent,
} from "../models/table-config.model";

@Component({
  selector: "fh-data-table",
  standalone: true,
  imports: [
    DatePipe,
    NgTemplateOutlet,
    MatTableModule,
    MatPaginatorModule,
    MatSortModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    ImageUrlDirective,
    RouterLink,
  ],
  templateUrl: "./data-table.component.html",
  styleUrls: ["./data-table.component.scss"],
})
export class DataTableComponent<T = any> implements AfterViewInit {
  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(MatPaginator) paginator!: MatPaginator;

  // Inputs
  config = input.required<TableConfig<T>>();
  sortProp = input<string>("");
  sortDir = input<SortDirection>("asc");
  pageIndex = input<number>(0);
  pageSize = input<number>(10);
  actions = input<TableAction<T>[]>([]);
  rowActionsTemplate = input<TemplateRef<any> | null>(null);

  // Outputs
  sortChange = output<TableSortEvent>();
  pageChange = output<PageEvent>();
  rowClick = output<T>();

  // Internal state
  dataSource = signal<MatTableDataSource<T>>(new MatTableDataSource<T>([]));
  displayedColumns = computed(() => {
    const columns = this.config().columns.map((col) => col.key);
    // Actions (view/edit/delete) lead the row.
    if (this.actions().length > 0 || this.rowActionsTemplate() || this.config().detailsRoute) {
      return ["actions", ...columns];
    }
    return columns;
  });

  highlightedRow = signal<T | null>(null);

  constructor(private router: Router) {}

  ngAfterViewInit(): void {
    // Sync data source with config
    const data = this.config().data || [];
    this.dataSource.set(new MatTableDataSource<T>(data));
  }

  ngOnChanges(): void {
    // Update data source when config changes
    const data = this.config().data || [];
    const ds = new MatTableDataSource<T>(data);
    this.dataSource.set(ds);
  }

  onSortChange(sort: Sort): void {
    if (!sort.active || sort.direction === "") {
      this.sortChange.emit({ prop: "", dir: "" as SortDirection });
      return;
    }

    const column = this.config().columns.find((col) => col.key === sort.active);
    if (column?.sortable !== false) {
      this.sortChange.emit({
        prop: sort.active,
        dir: sort.direction,
      });
    }
  }

  onPageChange(event: PageEvent): void {
    this.pageChange.emit(event);
  }

  onRowClick(row: T, event: Event): void {
    // Don't trigger row click if clicking on action buttons
    const target = event.target as HTMLElement;
    if (target.closest(".actions-cell") || target.closest("button")) {
      return;
    }

    // Don't trigger row click if actions column exists (actions present in the table)
    const hasActions = this.actions().length > 0 || this.rowActionsTemplate();
    if (hasActions) {
      return;
    }

    const cfg = this.config();
    if (cfg.highlightRowOnClick) {
      this.highlightedRow.set(row);
    }

    if (cfg.rowClickable && cfg.detailsRoute) {
      const route = this.getDetailsRoute(row);
      if (route) this.router.navigate([route]);
    }

    this.rowClick.emit(row);
  }

  /**
   * Resolves config().detailsRoute + getDetailsRouteParams into a concrete path, for
   * use as a real [routerLink] on the view/details action — a genuine <a href> is
   * what makes right-click "open in new tab" (and ctrl/cmd-click) work at all; a
   * (click) handler that calls router.navigate() never gives the browser anything to
   * open a new tab with, regardless of what icon sits on the button.
   */
  getDetailsRoute(row: T): string | null {
    const cfg = this.config();
    if (!cfg.detailsRoute) return null;
    const params = cfg.getDetailsRouteParams ? cfg.getDetailsRouteParams(row) : {};
    let route = cfg.detailsRoute;
    Object.keys(params).forEach((key) => {
      route = route.replace(`:${key}`, params[key]);
    });
    return route;
  }

  onActionClick(action: TableAction<T>, row: T, event: Event): void {
    event.stopPropagation();
    action.handler(row, event);
  }

  getCellValue(row: T, column: TableColumn<T>): any {
    const value = (row as any)[column.key];

    if (column.format) {
      return column.format(value, row);
    }

    if (column.type === "date" && value) {
      return new Date(value).toLocaleDateString();
    }

    if (column.type === "boolean") {
      return value ? "Yes" : "No";
    }

    return value ?? "—";
  }

  getCellClass(row: T, column: TableColumn<T>): string {
    if (typeof column.cssClass === "function") {
      return column.cssClass(row);
    }
    return column.cssClass || "";
  }

  shouldShowAction(action: TableAction<T>, row: T): boolean {
    if (action.show) {
      return action.show(row);
    }
    return true;
  }

  isActionDisabled(action: TableAction<T>, row: T): boolean {
    if (action.disabled) {
      return action.disabled(row);
    }
    return false;
  }

  isRowHighlighted(row: T): boolean {
    return this.highlightedRow() === row;
  }
}
